import React, { useContext } from "react";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Legend,
  Tooltip,
  Title,
  LineController,
  BarController
} from "chart.js";
import styled from "styled-components";
import { useString as s } from "../../../components/StringProvider";
import { SettingsContext } from "../../../components/SettingsProvider";
import Heading from "../../../components/Heading";
import Card from "../../../components/Card";
import { formatCurrencyString } from "../../../utils/formatting";
import { useMobileMediaQuery } from "../../../components/Responsive";
import {
  selectDiscoveryCashflow,
  selectDiscoveryCurrency,
  selectDiscoveryROI
} from "../../../store/reducers";
import { compose } from "redux";
import { connect } from "react-redux";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Legend,
  Tooltip,
  Title,
  ChartDataLabels,
  LineController,
  BarController
);

const COLORS = {
  netBenefit: "#54C9D4",
  investment: "#D55F55",
  investmentHover: "#FF7F7F",
  benefits: "#5E54D4",
  benefitsHover: "#817FFF",
  tickAndLabel: "#000032",
  tooltipBg: "#000000",
  tooltipText: "#FFFFFF",
  datalabelText: "#FFFFFF"
};

const FONT_FAMILY = '"Open Sans", sans-serif';

const ROIChart = ({ cashflow, roi, currency }) => {
  const isMobile = useMobileMediaQuery();
  const { settings } = useContext(SettingsContext);
  const localSettings = { ...settings, currency };
  const { evaluationPeriod } = roi;

  const labels = Array.from(
    { length: evaluationPeriod },
    (_, i) => `Year ${i + 1}`
  );

  const data = {
    labels,
    datasets: [
      {
        type: "line",
        label: s("discovery.roi.summary.chart.dataset1.label", "Net Benefit"),
        borderColor: COLORS.netBenefit,
        backgroundColor: COLORS.netBenefit,
        borderWidth: 2,
        fill: false,
        data: cashflow.netBenefitPerYear,
        lineTension: 0,
        pointRadius: 5,
        pointBackgroundColor: COLORS.netBenefit
      },
      {
        type: "bar",
        label: s("discovery.roi.summary.chart.dataset3.label", "Investment"),
        data: cashflow.investmentPerYear.map((num) => -Math.abs(num)),
        backgroundColor: COLORS.investment,
        borderColor: COLORS.investment,
        hoverBackgroundColor: COLORS.investmentHover
      },
      {
        type: "bar",
        label: s("discovery.roi.summary.chart.dataset4.label", "Benefits"),
        data: cashflow.benefitPerYear,
        backgroundColor: COLORS.benefits,
        borderColor: COLORS.benefits,
        hoverBackgroundColor: COLORS.benefitsHover
      }
    ]
  };

  const options = {
    maintainAspectRatio: false,
    animation: false,
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          color: COLORS.tickAndLabel,
          font: {
            size: 14,
            family: FONT_FAMILY
          },
          callback: (value) =>
            formatCurrencyString({
              value,
              settings: localSettings,
              options: { notation: "compact" }
            })
        }
      },
      x: {
        stacked: true,
        ticks: {
          color: COLORS.tickAndLabel,
          font: {
            size: 14,
            family: FONT_FAMILY
          },
          callback: (value) => `Year ${value + 1}`
        },
        grid: {
          drawBorder: false
        }
      }
    },
    plugins: {
      legend: {
        position: "top",
        labels: {
          usePointStyle: false,
          boxHeight: 12,
          boxWidth: 22,
          useBorderRadius: true,
          borderRadius: 2
        }
      },
      tooltip: {
        enabled: true,
        backgroundColor: COLORS.tooltipBg,
        titleColor: COLORS.tooltipText,
        bodyColor: COLORS.tooltipText,
        borderColor: "transparent",
        borderWidth: 0,
        displayColors: true,
        boxWidth: 12,
        boxHeight: 12,
        padding: 8,
        bodyFont: {
          weight: "bold",
          size: 14
        },
        callbacks: {
          labelColor: (context) => ({
            borderColor:
              context.dataset.backgroundColor || context.dataset.borderColor,
            backgroundColor:
              context.dataset.backgroundColor || context.dataset.borderColor,
            borderWidth: 2.5,
            borderRadius: 1
          }),
          label: (tooltipItem) => {
            const value = tooltipItem.raw;
            return (
              "  " +
              formatCurrencyString({
                value,
                settings: {
                  ...localSettings
                },
                options: { minimumFractionDigits: 0, maximumFractionDigits: 0 }
              })
            );
          }
        }
      },
      datalabels: {
        color: COLORS.datalabelText,
        font: {
          family: FONT_FAMILY,
          size: 16,
          style: "normal",
          weight: "700"
        },
        anchor: "center",
        align: "center",
        display: function (context) {
          if (context.dataset.type !== "bar") return false;

          const bar = context.chart.getDatasetMeta(context.datasetIndex).data[
            context.dataIndex
          ];

          if (!bar) return false;

          const barHeight = Math.abs(bar.base - bar.y);

          return barHeight >= 30;
        },
        formatter: function (value, context) {
          return formatCurrencyString({
            value,
            settings: { ...localSettings },
            options: { notation: "compact" }
          });
        }
      }
    }
  };

  return (
    <ROIChartContainer className={isMobile ? "mobile" : undefined}>
      <CardHeader>
        <Heading level="h5" bold>
          {s("discovery.roi.summary.chart.heading", "Breakdown by Years")}
        </Heading>
      </CardHeader>
      <CardBody>
        <Bar data={data} options={options} height={450} />
      </CardBody>
    </ROIChartContainer>
  );
};

const ROIChartContainer = styled(Card)`
  width: 100%;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  &.mobile {
    margin-top: 0px;
  }
`;

const CardHeader = styled.div``;

const CardBody = styled.div`
  width: 100%;
`;

const mapStateToProps = (state) => ({
  cashflow: selectDiscoveryCashflow(state),
  roi: selectDiscoveryROI(state),
  currency: selectDiscoveryCurrency(state)
});

export default compose(connect(mapStateToProps))(ROIChart);
